import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Input } from 'antd';
import {
  ArrowLeftOutlined, CheckCircleFilled, CloseCircleOutlined, KeyOutlined, LockOutlined, MailOutlined, UserOutlined,
} from '@ant-design/icons';
import { API_BASE_URL } from '../../utils/config';
import GirisKabugu, { Not } from './GirisKabugu';
import AltchaKutusu from './AltchaKutusu';

/**
 * Şifre yenileme (03.11.2026'da e-posta bağlantısına çevrildi; sunucu routes/password_reset.py).
 *   /sifre-sifirlama                 → kullanıcı adı + robot doğrulama → kayıtlı e-postaya 30 dk'lık bağlantı
 *   /sifre-sifirlama?anahtar=…       → yeni şifre (bağlantı tek kullanımlık)
 * Eski SMS kodlu akış kaldırıldı: SMS artık girişin ikinci adımı.
 */
const KURALLAR = [
  { ad: 'En az 8 karakter', uyar: (s) => s.length >= 8 },
  { ad: 'En az bir harf', uyar: (s) => /\p{L}/u.test(s) },
  { ad: 'En az bir rakam', uyar: (s) => /\d/.test(s) },
];

const istek = async (yol, secenekler) => {
  try {
    const y = await fetch(`${API_BASE_URL}${yol}`, secenekler);
    let veri = {};
    try { veri = await y.json(); } catch (e) { veri = {}; }
    return { ok: y.ok, status: y.status, ...veri };
  } catch (e) {
    return { ok: false, status: 0, detail: 'Sunucuya bağlanılamıyor. İnternet bağlantınızı kontrol edip tekrar deneyin.' };
  }
};

const Sonuc = ({ tur, ikon, baslik, children }) => (
  <div className="gk-sonuc">
    <div className={`gk-sonuc-ikon ${tur || ''}`} aria-hidden>{ikon}</div>
    <h2 className="gk-baslik">{baslik}</h2>
    {children}
  </div>
);

const TalepFormu = () => {
  const [yukleniyor, setYukleniyor] = useState(false);
  const [robotYuku, setRobotYuku] = useState(null);
  const [robotNo, setRobotNo] = useState(0);
  const [not, setNot] = useState(null);
  const [gonderildi, setGonderildi] = useState(null);

  const gonder = async ({ kullanici_adi: kullaniciAdi }) => {
    if (!robotYuku) {
      setNot({ tur: 'bilgi', metin: 'Robot doğrulaması tamamlanıyor, birkaç saniye sonra tekrar deneyin.' });
      return;
    }
    setYukleniyor(true);
    setNot(null);
    const s = await istek('/password-reset/talep', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kullanici_adi: kullaniciAdi.trim(), altcha: robotYuku }),
    });
    setYukleniyor(false);
    setRobotYuku(null);
    setRobotNo((n) => n + 1);
    if (s.ok) setGonderildi(s.detail);
    else setNot({ tur: 'hata', metin: s.detail || 'İstek gönderilemedi.' });
  };

  if (gonderildi) {
    return (
      <Sonuc ikon={<MailOutlined />} baslik="E-postanızı kontrol edin">
        <p className="gk-aciklama">{gonderildi}</p>
        <Not tur="bilgi">
          E-posta gelmezse ya da adresiniz sistemde kayıtlı değilse Bilgi Teknolojileri birimine başvurun.
        </Not>
        <Link to="/giris"><Button className="gk-birincil" type="primary">Giriş ekranına dön</Button></Link>
      </Sonuc>
    );
  }

  return (
    <>
      <h2 className="gk-baslik">Şifrenizi mi unuttunuz?</h2>
      <p className="gk-aciklama">
        Kullanıcı adınızı yazın; kayıtlı e-posta adresinize 30 dakika geçerli bir şifre yenileme bağlantısı gönderelim.
      </p>
      {not && <Not tur={not.tur}>{not.metin}</Not>}
      <Form name="sifre-talep" onFinish={gonder} size="large" layout="vertical" requiredMark={false} autoComplete="on">
        <Form.Item name="kullanici_adi" label="Kullanıcı adı"
          rules={[{ required: true, whitespace: true, message: 'Kullanıcı adınızı girin.' }]}>
          <Input prefix={<UserOutlined />} placeholder="Kullanıcı adınız" autoComplete="username" autoCapitalize="none"
            spellCheck={false} autoFocus />
        </Form.Item>
        <AltchaKutusu key={robotNo} onDogrulandi={setRobotYuku} adres="/password-reset/altcha" />
        <Button className="gk-birincil" type="primary" htmlType="submit" loading={yukleniyor}>
          Yenileme bağlantısı gönder
        </Button>
      </Form>
      <div className="gk-alt-satir">
        <Link className="gk-baglanti" to="/giris"><ArrowLeftOutlined /> Giriş ekranına dön</Link>
      </div>
    </>
  );
};

const YeniSifreFormu = ({ anahtar, onYeniBaglanti }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [durum, setDurum] = useState({ yukleniyor: true });
  const [yukleniyor, setYukleniyor] = useState(false);
  const [not, setNot] = useState(null);
  const sifre = Form.useWatch('sifre', form) || '';
  const kurallar = useMemo(() => KURALLAR.map((k) => ({ ...k, tamam: k.uyar(sifre) })), [sifre]);

  useEffect(() => {
    let iptal = false;
    istek(`/password-reset/anahtar/${encodeURIComponent(anahtar)}`).then((s) => {
      if (!iptal) setDurum(s.ok ? { gecerli: true, ad: s.ad } : { gecersiz: true, mesaj: s.detail });
    });
    return () => { iptal = true; };
  }, [anahtar]);

  const gonder = async ({ sifre: yeni }) => {
    setYukleniyor(true);
    setNot(null);
    const s = await istek('/password-reset/yeni-sifre', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anahtar, sifre: yeni }),
    });
    setYukleniyor(false);
    if (s.ok) setDurum({ tamam: true, mesaj: s.detail, oturumKapandi: !!s.oturum_kapatildi });
    else if (s.gecersiz) setDurum({ gecersiz: true, mesaj: s.detail });
    else setNot({ tur: 'hata', metin: s.detail || 'Şifre değiştirilemedi.' });
  };

  if (durum.yukleniyor) return <p className="gk-aciklama">Bağlantı kontrol ediliyor…</p>;
  if (durum.gecersiz) {
    return (
      <Sonuc tur="hata" ikon={<CloseCircleOutlined />} baslik="Bağlantı geçersiz">
        <p className="gk-aciklama">{durum.mesaj}</p>
        <Button className="gk-birincil" type="primary" onClick={onYeniBaglanti}>Yeni bağlantı iste</Button>
        <div className="gk-alt-satir">
          <Link className="gk-baglanti" to="/giris"><ArrowLeftOutlined /> Giriş ekranına dön</Link>
        </div>
      </Sonuc>
    );
  }
  if (durum.tamam) {
    return (
      <Sonuc tur="basari" ikon={<CheckCircleFilled />} baslik="Şifreniz değiştirildi">
        <p className="gk-aciklama">{durum.mesaj}</p>
        {durum.oturumKapandi && <Not tur="bilgi">Güvenliğiniz için açık oturumlarınız kapatıldı.</Not>}
        <Button className="gk-birincil" type="primary" onClick={() => navigate('/giris', { replace: true })}>
          Giriş yap
        </Button>
      </Sonuc>
    );
  }

  return (
    <>
      <h2 className="gk-baslik">Yeni şifrenizi belirleyin</h2>
      <p className="gk-aciklama">
        {durum.ad ? <>Merhaba <b>{durum.ad}</b>, </> : null}Fedora için yeni bir şifre belirleyin. Bağlantı yalnız bir kez
        kullanılabilir.
      </p>
      {not && <Not tur={not.tur}>{not.metin}</Not>}
      <Form form={form} name="yeni-sifre" onFinish={gonder} size="large" layout="vertical" requiredMark={false}>
        <Form.Item name="sifre" label="Yeni şifre" validateTrigger="onBlur"
          rules={[{ required: true, message: 'Yeni şifrenizi girin.' },
            { validator: (_, v) => (KURALLAR.every((k) => k.uyar(v || ''))
              ? Promise.resolve() : Promise.reject(new Error('Şifre aşağıdaki kuralların hepsine uymalı.'))) }]}>
          <Input.Password prefix={<LockOutlined />} placeholder="Yeni şifreniz" autoComplete="new-password" autoFocus />
        </Form.Item>
        <ul className="gk-kurallar" aria-label="Şifre kuralları">
          {kurallar.map((k) => (
            <li key={k.ad} className={k.tamam ? 'tamam' : ''}>
              {k.tamam ? <CheckCircleFilled /> : <span className="gk-kural-bos" aria-hidden />}
              {k.ad}<span className="sr-only">{k.tamam ? ' (sağlandı)' : ' (eksik)'}</span>
            </li>
          ))}
        </ul>
        <Form.Item name="tekrar" label="Yeni şifre (tekrar)" dependencies={['sifre']}
          rules={[{ required: true, message: 'Yeni şifrenizi tekrar girin.' },
            ({ getFieldValue }) => ({
              validator: (_, v) => (!v || getFieldValue('sifre') === v
                ? Promise.resolve() : Promise.reject(new Error('Şifreler aynı değil.'))),
            })]}>
          <Input.Password prefix={<KeyOutlined />} placeholder="Yeni şifrenizi tekrar girin" autoComplete="new-password" />
        </Form.Item>
        <Button className="gk-birincil" type="primary" htmlType="submit" loading={yukleniyor}>Şifremi değiştir</Button>
      </Form>
    </>
  );
};

const SifreSifirlamaPage = () => {
  // Anahtar adres çubuğundan hemen silinir (tarayıcı geçmişinde kalmasın); sayfa yenilenirse e-postadaki bağlantıya
  // yeniden tıklamak yeterli — bağlantı kullanılana kadar geçerli.
  const [anahtar, setAnahtar] = useState(() => new URLSearchParams(window.location.search).get('anahtar'));
  useEffect(() => {
    if (window.location.search) window.history.replaceState({}, document.title, window.location.pathname);
    const geri = () => setAnahtar(new URLSearchParams(window.location.search).get('anahtar'));
    window.addEventListener('popstate', geri);
    return () => window.removeEventListener('popstate', geri);
  }, []);

  return (
    <GirisKabugu>
      <style>{'.sr-only{position:absolute;width:1px;height:1px;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0}'}</style>
      {anahtar ? <YeniSifreFormu anahtar={anahtar} onYeniBaglanti={() => setAnahtar(null)} /> : <TalepFormu />}
    </GirisKabugu>
  );
};

export default SifreSifirlamaPage;
